/**
 * Shared client form field sections
 * Rendered by both the full Client page form and the Quick Client form in the
 * visit wizard, so both entry points collect the exact same client data.
 */

import { useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { User, MapPin, Phone, Info } from 'lucide-react'

import { useProvinces, useCitiesByProvince, useBarangaysByCity } from '@/hooks/useAddresses'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePickerField } from '@/components/ui/date-picker'
import { PhoneInput } from '@/components/ui/phone-input'
import {
  RELIGIONS,
  EDUCATIONAL_ATTAINMENT,
  INDIGENOUS_GROUPS,
  CITIZENSHIPS,
  GENDERS,
  CIVIL_STATUS_OPTIONS,
} from '@/config/client-dropdowns'
import { ClientFormData } from '@/lib/schemas/client-form'

interface ClientFormFieldsProps {
  form: UseFormReturn<ClientFormData>
  /** Province/city names to pre-select the cascading address dropdowns when editing */
  initialProvinceName?: string | null
  initialCityName?: string | null
}

export function ClientFormFields({ form, initialProvinceName, initialCityName }: ClientFormFieldsProps) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form

  const religionValue = watch('religion')
  const indigenousGroupValue = watch('indigenous_group')
  const showReligionOther = religionValue === 'Others'
  const showIndigenousOther = indigenousGroupValue === 'Others'

  // State for cascading address dropdowns
  const [selectedProvinceId, setSelectedProvinceId] = useState<number | null>(null)
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null)

  // Fetch address data
  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces()
  const { data: cities, isLoading: isLoadingCities } = useCitiesByProvince(selectedProvinceId)
  const { data: barangays, isLoading: isLoadingBarangays } = useBarangaysByCity(selectedCityId)

  // Initialize address dropdowns when editing (match province/city names to IDs)
  useEffect(() => {
    if (initialProvinceName && provinces) {
      const province = provinces.find((p) => p.province_name === initialProvinceName)
      if (province) {
        setSelectedProvinceId(province.id)
      }
    }
  }, [initialProvinceName, provinces])

  useEffect(() => {
    if (initialCityName && cities) {
      const city = cities.find((c) => c.city_municipality_name === initialCityName)
      if (city) {
        setSelectedCityId(city.id)
      }
    }
  }, [initialCityName, cities])

  return (
    <>
      {/* Personal Information */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
          <User className="h-5 w-5" />
          Personal Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* First Name */}
          <div className="space-y-2">
            <Label htmlFor="firstname">
              First Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="firstname"
              {...register('firstname')}
              className={errors.firstname ? 'border-red-500' : ''}
            />
            {errors.firstname && (
              <p className="text-sm text-red-500">{errors.firstname.message}</p>
            )}
          </div>

          {/* Middle Name */}
          <div className="space-y-2">
            <Label htmlFor="middlename">Middle Name</Label>
            <Input id="middlename" {...register('middlename')} />
          </div>

          {/* Last Name */}
          <div className="space-y-2">
            <Label htmlFor="lastname">
              Last Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="lastname"
              {...register('lastname')}
              className={errors.lastname ? 'border-red-500' : ''}
            />
            {errors.lastname && (
              <p className="text-sm text-red-500">{errors.lastname.message}</p>
            )}
          </div>

          {/* Gender */}
          <div className="space-y-2">
            <Label htmlFor="gender">
              Gender <span className="text-red-500">*</span>
            </Label>
            <Select value={watch('gender') || undefined} onValueChange={(value) => setValue('gender', value as ClientFormData['gender'])}>
              <SelectTrigger className={errors.gender ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                {GENDERS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.gender && (
              <p className="text-sm text-red-500">{errors.gender.message}</p>
            )}
          </div>

          {/* Birthday */}
          <div className="space-y-2">
            <Label htmlFor="birthday">
              Date of Birth <span className="text-red-500">*</span>
            </Label>
            <DatePickerField
              value={watch('birthday')}
              onChange={(value) => setValue('birthday', value || '')}
              placeholder="Select date of birth"
            />
            {errors.birthday && (
              <p className="text-sm text-red-500">{errors.birthday.message}</p>
            )}
          </div>

          {/* Civil Status */}
          <div className="space-y-2">
            <Label htmlFor="civil_status">
              Civil Status <span className="text-red-500">*</span>
            </Label>
            <Select value={watch('civil_status') || undefined} onValueChange={(value) => setValue('civil_status', value)}>
              <SelectTrigger className={errors.civil_status ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select civil status" />
              </SelectTrigger>
              <SelectContent>
                {CIVIL_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.civil_status && (
              <p className="text-sm text-red-500">{errors.civil_status.message}</p>
            )}
          </div>

          {/* Citizenship */}
          <div className="space-y-2">
            <Label htmlFor="citizenship">
              Citizenship <span className="text-red-500">*</span>
            </Label>
            <Select value={watch('citizenship') || undefined} onValueChange={(value) => setValue('citizenship', value)}>
              <SelectTrigger className={errors.citizenship ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select citizenship" />
              </SelectTrigger>
              <SelectContent>
                {CITIZENSHIPS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.citizenship && (
              <p className="text-sm text-red-500">{errors.citizenship.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
          <Phone className="h-5 w-5" />
          Contact Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              type="email"
              id="email"
              {...register('email')}
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          {/* Contact Number */}
          <div className="space-y-2">
            <Label htmlFor="contact_number">Contact Number</Label>
            <PhoneInput
              id="contact_number"
              value={watch('contact_number') || ''}
              onChange={(value) => setValue('contact_number', value || '')}
              defaultCountry="PH"
              placeholder="Enter phone number"
              className={errors.contact_number ? 'border-red-500' : ''}
            />
            {errors.contact_number && (
              <p className="text-sm text-red-500">{errors.contact_number.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Address Information */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
          <MapPin className="h-5 w-5" />
          Address Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Street Name */}
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="street_name">Street/House Number</Label>
            <Input id="street_name" {...register('street_name')} />
          </div>

          {/* Province */}
          <div className="space-y-2">
            <Label htmlFor="province">
              Province <span className="text-red-500">*</span>
            </Label>
            <Select
              value={selectedProvinceId?.toString() || undefined}
              onValueChange={(value) => {
                const provinceId = parseInt(value)
                setSelectedProvinceId(provinceId)
                setSelectedCityId(null) // Reset city and barangay when province changes
                const province = provinces?.find((p) => p.id === provinceId)
                setValue('province', province?.province_name || '')
                setValue('city', '') // Reset dependent fields
                setValue('barangay', '')
              }}
              disabled={isLoadingProvinces}
            >
              <SelectTrigger className={errors.province ? 'border-red-500' : ''}>
                <SelectValue placeholder={isLoadingProvinces ? 'Loading...' : 'Select province'} />
              </SelectTrigger>
              <SelectContent>
                {provinces?.map((province) => (
                  <SelectItem key={province.id} value={province.id.toString()}>
                    {province.province_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.province && (
              <p className="text-sm text-red-500">{errors.province.message}</p>
            )}
          </div>

          {/* City */}
          <div className="space-y-2">
            <Label htmlFor="city">
              City/Municipality <span className="text-red-500">*</span>
            </Label>
            <Select
              value={selectedCityId?.toString() || undefined}
              onValueChange={(value) => {
                const cityId = parseInt(value)
                setSelectedCityId(cityId)
                const city = cities?.find((c) => c.id === cityId)
                setValue('city', city?.city_municipality_name || '')
                setValue('barangay', '') // Reset barangay when city changes
              }}
              disabled={!selectedProvinceId || isLoadingCities}
            >
              <SelectTrigger className={errors.city ? 'border-red-500' : ''}>
                <SelectValue
                  placeholder={
                    !selectedProvinceId
                      ? 'Select province first'
                      : isLoadingCities
                      ? 'Loading...'
                      : 'Select city/municipality'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {cities?.map((city) => (
                  <SelectItem key={city.id} value={city.id.toString()}>
                    {city.city_municipality_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.city && (
              <p className="text-sm text-red-500">{errors.city.message}</p>
            )}
          </div>

          {/* Barangay */}
          <div className="space-y-2">
            <Label htmlFor="barangay">Barangay</Label>
            <Select
              value={watch('barangay') || undefined}
              onValueChange={(value) => {
                const barangay = barangays?.find((b) => b.barangay_name === value)
                setValue('barangay', barangay?.barangay_name || value)
              }}
              disabled={!selectedCityId || isLoadingBarangays}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    !selectedCityId
                      ? 'Select city first'
                      : isLoadingBarangays
                      ? 'Loading...'
                      : 'Select barangay'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {barangays?.map((barangay) => (
                  <SelectItem key={barangay.id} value={barangay.barangay_name}>
                    {barangay.barangay_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Postal Code */}
          <div className="space-y-2">
            <Label htmlFor="postal_code">Postal Code</Label>
            <Input
              id="postal_code"
              {...register('postal_code')}
              placeholder="1000"
              maxLength={4}
              className={errors.postal_code ? 'border-red-500' : ''}
            />
            {errors.postal_code && (
              <p className="text-sm text-red-500">{errors.postal_code.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Other Information */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
          <Info className="h-5 w-5" />
          Other Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Religion */}
          <div className="space-y-2">
            <Label htmlFor="religion">Religion</Label>
            <Select value={watch('religion') || undefined} onValueChange={(value) => setValue('religion', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select religion" />
              </SelectTrigger>
              <SelectContent>
                {RELIGIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Religion Other (conditional) */}
          {showReligionOther && (
            <div className="space-y-2">
              <Label htmlFor="religion_other">
                Please Specify Religion <span className="text-red-500">*</span>
              </Label>
              <Input
                id="religion_other"
                {...register('religion_other')}
                placeholder="Please specify"
              />
            </div>
          )}

          {/* Educational Attainment */}
          <div className="space-y-2">
            <Label htmlFor="educational_attainment">
              Educational Attainment <span className="text-red-500">*</span>
            </Label>
            <Select value={watch('educational_attainment') || undefined} onValueChange={(value) => setValue('educational_attainment', value)}>
              <SelectTrigger className={errors.educational_attainment ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select educational attainment" />
              </SelectTrigger>
              <SelectContent>
                {EDUCATIONAL_ATTAINMENT.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.educational_attainment && (
              <p className="text-sm text-red-500">{errors.educational_attainment.message}</p>
            )}
          </div>

          {/* Indigenous Group */}
          <div className="space-y-2">
            <Label htmlFor="indigenous_group">Indigenous Group</Label>
            <Select value={watch('indigenous_group') || undefined} onValueChange={(value) => setValue('indigenous_group', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select indigenous group (optional)" />
              </SelectTrigger>
              <SelectContent>
                {INDIGENOUS_GROUPS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Indigenous Group Other (conditional) */}
          {showIndigenousOther && (
            <div className="space-y-2">
              <Label htmlFor="indigenous_group_other">
                Please Specify Indigenous Group <span className="text-red-500">*</span>
              </Label>
              <Input
                id="indigenous_group_other"
                {...register('indigenous_group_other')}
                placeholder="Please specify"
              />
            </div>
          )}

          {/* Monthly Income */}
          <div className="space-y-2">
            <Label htmlFor="monthly_income">
              Monthly Income (PHP) <span className="text-red-500">*</span>
            </Label>
            <Input
              type="number"
              step="0.01"
              id="monthly_income"
              {...register('monthly_income', { valueAsNumber: true })}
              placeholder="0.00"
              className={errors.monthly_income ? 'border-red-500' : ''}
            />
            {errors.monthly_income && (
              <p className="text-sm text-red-500">{errors.monthly_income.message}</p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
